import { addContentSlide, insertSlideFromBase64 } from "../hooks/slideInsert";
import {
  SlideApiError,
  slidePptxBase64,
  streamSlideGeneration,
  type ContractTone,
  type GeneratePresentationRequest,
  type SlideBrief,
  type SlideStreamEvents,
  type SlideXmlResult,
  type ThemeColors,
} from "./slideGenerationApi";
import type {
  CreateSlideConfig,
  GenerationRun,
  GeneratorCallbacks,
  SlideGenerator,
  SlidePlan,
  Tone,
} from "../hooks/types";
import type { AuthorizedFetch } from "../../../services/deckorateApi";

/**
 * Backend-backed SlideGenerator. Consumes the Slide Generation SSE API and
 * adapts its events onto the wizard's generator callbacks, so the wizard and
 * progress UI are identical whether generation is mocked or real.
 *
 * Current scope / follow-ups (contract not finalised):
 *   - Uses the JSON POST /v1/deckorate/slides/create-slide-ai path (topic / document_text).
 *     The multipart /upload path exists in the client but isn't wired here yet
 *     (files can't cross the dialog→pane messageParent boundary).
 *   - Inserts each completed slide as a titled slide from the outline points.
 *     Rendering the returned OOXML (<p:spTree>) as a designed slide needs PPTX
 *     packaging (insertSlidesFromBase64) — the streamed `xml` is retained via
 *     `onSlideXml`/`slide_complete` for when that lands.
 */

const TONE_MAP: Record<Tone, ContractTone> = {
  Professional: "professional",
  Conversational: "casual",
  Persuasive: "persuasive",
  Inspirational: "inspirational",
  Technical: "educational",
  Friendly: "casual",
};

const API_MIN_SLIDES = 3;
const API_MAX_SLIDES = 30;

interface ApiGeneratorDeps {
  /** Deckorate backend base URL (config.deckorateApiBaseUrl). */
  baseUrl: string;
  /** Shared authorised fetch (bearer + APIM key + 401 refresh) from useDeckorateHttp(). */
  authorizedFetch: AuthorizedFetch;
  /** Active deck's theme colours, sent as theme_colors so slides match the brand. */
  themeColors?: ThemeColors;
  /** Insert one completed slide. Defaults to a titled slide from outline points. */
  insertSlide?: (result: SlideXmlResult, brief: SlideBrief | undefined) => Promise<void>;
}

function toRequest(config: CreateSlideConfig): GeneratePresentationRequest {
  const slideCount = Math.min(API_MAX_SLIDES, Math.max(API_MIN_SLIDES, config.slideCount));
  // Document input goes via blob (backend extracts). Until the blob upload
  // mechanism lands, `blobName` is unset so we fall back to the topic.
  const useDocument = config.source === "upload" && Boolean(config.blobName);
  return {
    input_type: useDocument ? "document" : "topic",
    topic: config.topic.trim() || config.documents[0]?.name || "Untitled deck",
    blob_container: useDocument ? config.blobContainer : undefined,
    blob_name: useDocument ? config.blobName : undefined,
    audience: config.audience || undefined,
    slide_count: slideCount,
    tone: TONE_MAP[config.tone],
    include_cover: config.coverSlide,
    include_closing: config.closingSlide,
  };
}

const defaultInsert = (result: SlideXmlResult, brief: SlideBrief | undefined) => {
  const pptx = slidePptxBase64(result);
  // Preferred path: insert the backend's designed base64 .pptx verbatim.
  // Fallback: a placeholder slide from the outline points.
  return pptx
    ? insertSlideFromBase64(pptx)
    : addContentSlide(brief?.title ?? result.title, brief?.main_points ?? []);
};

/** Map a backend outline brief to a wizard SlidePlan (1-based index). */
function toSlidePlan(s: SlideBrief): SlidePlan {
  return {
    index: s.slide_idx + 1,
    title: s.title,
    status: "waiting",
  };
}

function collectBriefAndPlan(s: SlideBrief, briefs: Map<number, SlideBrief>): SlidePlan {
  briefs.set(s.slide_idx, s);
  return toSlidePlan(s);
}

/**
 * Insert one completed slide onto the deck and reflect success/failure on the
 * wizard. Runs on the serialised insert chain so PowerPoint.run calls don't overlap.
 */
async function insertSlideWithCallback(
  insertSlide: (result: SlideXmlResult, brief: SlideBrief | undefined) => Promise<void>,
  result: SlideXmlResult,
  brief: SlideBrief | undefined,
  idx: number,
  cb: GeneratorCallbacks,
): Promise<void> {
  try {
    await insertSlide(result, brief);
    cb.onSlideUpdate(idx, "done", "Added to deck");
  } catch (e) {
    console.error(`[create-slide] insert failed for slide ${idx}:`, e);
    cb.onSlideUpdate(idx, "error", e instanceof Error ? e.message : "Insert failed");
  }
}

/** Handle the outline event: record briefs, publish plans, and return the slide total. */
function handleOutline(
  data: { slides: SlideBrief[] },
  briefs: Map<number, SlideBrief>,
  cb: GeneratorCallbacks,
): number {
  const total = data.slides.length;
  const plans = data.slides.map((slide) => collectBriefAndPlan(slide, briefs));

  cb.onPlan(plans);
  cb.onProgress(0, total);
  cb.onPhase("generating");

  return total;
}

function handleSlideComplete(
  result: SlideXmlResult,
  briefs: Map<number, SlideBrief>,
  cb: GeneratorCallbacks,
  insertSlide: (result: SlideXmlResult, brief: SlideBrief | undefined) => Promise<void>,
  insertChain: Promise<void>,
): Promise<void> {
  const idx = result.slide_idx + 1;

  if (result.status === "failed") {
    cb.onSlideUpdate(idx, "error", result.error ?? "Slide failed");
    return insertChain;
  }

  cb.onSlideUpdate(idx, "generating", "Adding to deck");

  const brief = briefs.get(result.slide_idx);

  return insertChain.then(() =>
    insertSlideWithCallback(insertSlide, result, brief, idx, cb),
  );
}

export function createApiGenerator(deps: ApiGeneratorDeps): SlideGenerator {
  const insertSlide = deps.insertSlide ?? defaultInsert;

  return {
    generate(config: CreateSlideConfig, cb: GeneratorCallbacks): GenerationRun {
      const controller = new AbortController();
      const briefs = new Map<number, SlideBrief>();
      let total = 0;
      // Set when a fatal stream error or an invalid outline is seen, so the
      // post-stream transition below does NOT overwrite the error phase with
      // "done" (which would show "Deck created" for a failed generation).
      let fatal = false;
      // Serialise slide inserts so PowerPoint.run calls don't overlap.
      let insertChain: Promise<void> = Promise.resolve();

      void (async () => {
        try {
          cb.onPhase("outlining");
          const events: SlideStreamEvents = {
              onStart: () => cb.onPhase("outlining"),
              onOutline: (data) => {
                // Guard contract drift: a valid-JSON outline frame missing/renaming
                // `slides` would otherwise throw inside the SSE parser's try/catch
                // and be swallowed as a "malformed frame", ending in a bogus
                // "0 of 0 slides" success. Surface it as a real error instead.
                if (!data || !Array.isArray(data.slides)) {
                  fatal = true;
                  console.error("[create-slide] outline event missing `slides`:", data);
                  cb.onError("The server returned an invalid slide outline.");
                  cb.onPhase("error");
                  return;
                }
                total = handleOutline(data, briefs, cb);
              },
              onSlideStart: (d) => {
                cb.onProgress(d.slide_idx + 1, total);
                cb.onSlideUpdate(d.slide_idx + 1, "generating", "Writing content");
              },
              onStatus: () => {
                // e.g. { phase: "slide_retry", message: "Slide 1: fixing XML…" }
                cb.onPhase("generating");
              },
              onSlideComplete: (result) => {
                insertChain = handleSlideComplete(result, briefs, cb, insertSlide, insertChain);
              },
              onError: (d) => {
                // Mark fatal so the post-stream success transition is skipped —
                // otherwise "done" would clobber this error phase.
                fatal = true;
                console.error(`[create-slide] stream error (${d.code}):`, d.message);
                cb.onError(d.message);
                cb.onPhase("error");
              },
              // `done` summary is handled after the stream + inserts settle.
          };
          const opts = {
            baseUrl: deps.baseUrl,
            authorizedFetch: deps.authorizedFetch,
            signal: controller.signal,
          };

          await streamSlideGeneration(
            { ...toRequest(config), theme_colors: deps.themeColors },
            events,
            opts,
          );

          await insertChain;
          if (controller.signal.aborted) return cb.onPhase("cancelled");
          // A fatal stream error / invalid outline already set the error phase —
          // don't overwrite it with a success ("done") transition.
          if (fatal) return;
          cb.onProgress(total, total);
          cb.onPhase("done");
        } catch (e) {
          if (controller.signal.aborted || (e instanceof DOMException && e.name === "AbortError")) {
            return cb.onPhase("cancelled");
          }
          let msg: string;
          if (e instanceof SlideApiError) {
            msg = e.message;
          } else {
            msg = e instanceof Error ? e.message : "Generation failed";
          }
          console.error("[create-slide] generation failed:", e);
          cb.onError(msg);
          cb.onPhase("error");
        }
        return undefined;
      })();

      return { cancel: () => controller.abort() };
    },
  };
}
